import { findAncestor } from '../utils';

type ParentWithAttributeArgs = {
  attr: string;
  value?: string;
  maxLevelUp?: number;
  element: Element;
};

export const parentWithAttribute = ({
  attr,
  value,
  maxLevelUp,
  element,
}: ParentWithAttributeArgs): Element | undefined =>
  findAncestor({
    element,
    predicate: (el) =>
      el.hasAttribute(attr) && (!value || el.getAttribute(attr) === value),
    maxLevelUp,
  });

type ParentWithClassArgs = {
  className: string;
  maxLevelUp?: number;
  element: Element;
};

export const parentWithClass = ({
  className,
  maxLevelUp,
  element,
}: ParentWithClassArgs): Element | undefined =>
  findAncestor({
    element,
    predicate: (el) => el.classList.contains(className),
    maxLevelUp,
  });

type ParentWithRoleArgs = {
  role: string;
  maxLevelUp?: number;
  element: Element;
};

export const parentWithRole = ({
  role,
  maxLevelUp,
  element,
}: ParentWithRoleArgs): Element | undefined =>
  findAncestor({
    element,
    predicate: (el) => el.getAttribute('role') === role,
    maxLevelUp,
  });

type AggregateAncestorDataArgs<DATA> = {
  element: Element;
  getter: (
    element: Element,
    level: number,
    aggregate: DATA[]
  ) => DATA | undefined;
  stopWhen?: (element: Element, level: number, aggregate: DATA[]) => boolean;
  skipSelf?: boolean;
};

export const aggregateAncestorData = <DATA>({
  element,
  getter,
  stopWhen,
  skipSelf,
}: AggregateAncestorDataArgs<DATA>): DATA[] => {
  const result: DATA[] = [];
  let current = skipSelf ? element.parentElement : element;

  while (current) {
    const data = getter(current, result.length, result);
    if (data !== undefined) {
      result.push(data);
    }
    if (stopWhen?.(current, result.length, result)) break;
    current = current.parentElement;
  }

  return result;
};

const collectText = (node: Node, counter: { remaining: number }): string => {
  let result = '';

  for (const child of Array.from(node.childNodes)) {
    if (counter.remaining <= 0) {
      console.error('getDisplayValue: Max elements limit reached');
      break;
    }

    if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent + ' ';
      counter.remaining--;
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      result += collectText(child, counter).trim() + ' ';
    }
  }

  return result;
};

/**
 * Extracts text content from a DOM node, with spaces between elements.
 * @param node The DOM node to extract text from
 * @param maxElements Maximum number of elements to process (default: 50)
 */
export const getDisplayValue = (
  node: Node | null | undefined,
  maxElements = 50
): string | undefined => {
  if (!node?.childNodes) {
    return undefined;
  }

  return collectText(node, { remaining: maxElements }).trim();
};
